import { useMemo } from "react";
// Entrada `nohighlight`: mesmo editor sem o syntax highlighter (refractor/prism),
// que é pesado e desnecessário para descrições de tarefa — reduz muito o bundle.
import MDEditor from "@uiw/react-md-editor/nohighlight";
import remarkBreaks from "remark-breaks";

import { Label } from "@/components/ui/label";
import { useTheme } from "@/lib/theme-context";
import { cn } from "@/lib/utils";
import { ErrorTextComponent } from "../error-text-component";
import { HelperTextComponent } from "../helper-text-component";
// Overrides de tema do editor (carrega depois do CSS da lib). Ver arquivo.
import "./FormMarkdownEditor.css";

interface FormMarkdownEditorProps {
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  id?: string;
  placeholder?: string;
  height?: number;
}

/** Resolve 'system' para 'dark' | 'light' lendo o `prefers-color-scheme`. */
function resolveColorMode(theme: string): "dark" | "light" {
  if (theme === "dark") return "dark";
  if (theme === "light") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Editor de descrição em markdown sobre `@uiw/react-md-editor`: toolbar completa
 * (negrito, títulos, listas, link, código, citação…), atalhos de teclado e
 * alternância editar/pré-visualizar embutidas. A descrição é salva como string
 * markdown — o mesmo formato que o detalhe da tarefa renderiza. Mantém a interface
 * de props compatível com o `Controller` do RHF.
 */
export function FormMarkdownEditor({
  label,
  error,
  required,
  helperText,
  value = "",
  onChange,
  onBlur,
  id,
  placeholder,
  height = 240,
}: FormMarkdownEditorProps) {
  const { theme } = useTheme();
  const colorMode = useMemo(() => resolveColorMode(theme), [theme]);

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex h-[20px] items-center gap-2">
          <Label
            htmlFor={id}
            className={cn("text-sm font-medium text-foreground", error && "text-destructive")}
          >
            {required && <span className="mr-1 text-destructive">*</span>}
            {label}
          </Label>
        </div>
      )}

      {/* `data-color-mode` define claro/escuro do editor; o tema das superfícies
          é casado com o app via `FormMarkdownEditor.css` (classe `fmd-md-editor`).
          O wrapper espelha o fundo dos demais inputs (`dark:bg-input/30`). */}
      <div
        data-color-mode={colorMode}
        onBlur={onBlur}
        className={cn(
          "overflow-hidden rounded-md border border-input bg-transparent shadow-sm transition-colors dark:bg-input/30",
          "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50",
          error &&
            "border-destructive focus-within:border-destructive focus-within:ring-destructive/40",
        )}
      >
        <MDEditor
          value={value}
          onChange={(v) => onChange?.(v ?? "")}
          height={height}
          preview="edit"
          visibleDragbar={false}
          textareaProps={{ id, placeholder }}
          previewOptions={{ remarkPlugins: [remarkBreaks] }}
          className="fmd-md-editor !border-0 !shadow-none"
        />
      </div>

      <ErrorTextComponent error={error ?? ""} />
      <HelperTextComponent text={helperText ?? ""} error={error} />
    </div>
  );
}
